import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, Check, X } from "lucide-react";

import { useNotifications } from "../../Services/NotificationsContext";
import {
  acceptFollowRequest,
  fetchFollowRequests,
  rejectFollowRequest,
} from "../../Services/SocialService";
import { notificationCopy } from "../../utils/notifications";
import { formatInitials, timeAgo } from "../../utils/format";
import type { FollowRequest } from "../../data/types";
import "./NotificationBell.css";

export default function NotificationBell() {
  const { items, unreadCount, pendingRequests, markAllRead, refresh } =
    useNotifications();

  const [isOpen, setIsOpen] = useState(false);
  const [requests, setRequests] = useState<FollowRequest[]>([]);
  const [busyRequestId, setBusyRequestId] = useState<string | null>(null);

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const badgeCount = unreadCount + pendingRequests;

  const loadRequests = useCallback(async () => {
    try {
      setRequests(await fetchFollowRequests());
    } catch (e) {
      console.error("follow requests fetch failed", e);
    }
  }, []);

  const toggle = () => {
    const next = !isOpen;
    setIsOpen(next);

    if (next) {
      markAllRead();
      loadRequests();
    }
  };

  // Väntande förfrågningar har sin egen sektion med ja/nej-knappar, så samma
  // person ska inte dyka upp en andra gång längre ned i listan.
  const listItems = items.filter((n) => n.type !== "FOLLOW_REQUEST");

  // Klick utanför stänger dropdownen.
  useEffect(() => {
    if (!isOpen) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen]);

  const resolveRequest = async (
    request: FollowRequest,
    action: "accept" | "reject"
  ) => {
    setBusyRequestId(request.id);

    try {
      if (action === "accept") {
        await acceptFollowRequest(request.id);
      } else {
        await rejectFollowRequest(request.id);
      }

      setRequests((current) => current.filter((r) => r.id !== request.id));
      await refresh();
    } catch (e) {
      console.error("resolve follow request failed", e);
    } finally {
      setBusyRequestId(null);
    }
  };

  return (
    <div className="notification-bell" ref={wrapperRef}>
      <button
        type="button"
        className={`notification-bell-button ${isOpen ? "active" : ""}`}
        onClick={toggle}
        aria-label={
          badgeCount > 0
            ? `Notifications, ${badgeCount} new`
            : "Notifications"
        }
        aria-expanded={isOpen}
      >
        <Bell size={20} strokeWidth={1.9} />

        {badgeCount > 0 && (
          <span className="notification-bell-badge">
            {badgeCount > 99 ? "99+" : badgeCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="notification-dropdown">
          <div className="notification-dropdown-header">
            <h3>Notifications</h3>
          </div>

          {requests.length > 0 && (
            <div className="notification-requests">
              {requests.map((request) => (
                <div key={request.id} className="notification-request">
                  <div className="notification-media">
                    {request.user.avatarUrl ? (
                      <img src={request.user.avatarUrl} alt="" />
                    ) : (
                      <span>{formatInitials(request.user.displayName)}</span>
                    )}
                  </div>

                  <div className="notification-text">
                    <strong>
                      <span className="notification-actor">
                        {request.user.displayName}
                      </span>{" "}
                      <span className="notification-action">
                        wants to follow you
                      </span>
                    </strong>
                    <small>{timeAgo(request.createdAt)}</small>
                  </div>

                  <div className="notification-request-actions">
                    <button
                      type="button"
                      className="request-accept"
                      disabled={busyRequestId === request.id}
                      onClick={() => resolveRequest(request, "accept")}
                      aria-label={`Accept ${request.user.displayName}`}
                    >
                      <Check size={15} strokeWidth={2.4} />
                    </button>

                    <button
                      type="button"
                      className="request-reject"
                      disabled={busyRequestId === request.id}
                      onClick={() => resolveRequest(request, "reject")}
                      aria-label={`Decline ${request.user.displayName}`}
                    >
                      <X size={15} strokeWidth={2.4} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {listItems.length === 0 && requests.length === 0 ? (
            <div className="notification-empty">
              <p>Nothing here yet</p>
              <span>
                Check-ins, follows and new tracks will show up right here.
              </span>
            </div>
          ) : (
            <div className="notification-list">
              {listItems.map((notification) => {
                const copy = notificationCopy(notification);

                return (
                  <Link
                    key={notification.id}
                    to={copy.href}
                    className={`notification-item ${
                      notification.read ? "" : "unread"
                    }`}
                    onClick={() => setIsOpen(false)}
                  >
                    <div className="notification-media">
                      {copy.imageUrl ? (
                        <img src={copy.imageUrl} alt="" />
                      ) : (
                        <span>{formatInitials(copy.initialsFrom)}</span>
                      )}
                    </div>

                    <div className="notification-text">
                      <strong>
                        <span className="notification-actor">{copy.actor}</span>{" "}
                        <span className="notification-action">
                          {copy.action}
                        </span>
                        {copy.subject && (
                          <>
                            {" "}
                            <span className="notification-subject">
                              {copy.subject}
                            </span>
                          </>
                        )}
                      </strong>

                      <small>
                        {copy.meta} · {timeAgo(notification.createdAt)}
                      </small>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
